import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import useFetchPortalDocument from '../../../../hooks/useApi/useFetchPortalDocument';
import PortalGuideForm from './PortalGuideForm';
import PortalGuideResult from './PortalGuideResult';
import styles from '../Video/VideoContainer.module.scss';
import { usePartnerData } from '@/providers/PartnerData.provider';
type FC = FunctionComponent;

const { Box, Title, Button, ViewPDFModal, Icon } = vp;


const PortalGuideContainer: FC = () => {
  const [documents, fetchPortalDocument] = useFetchPortalDocument({});
  const {
    response: { partnerTypeLabel },
  } = usePartnerData();
  const [activeTab, setActiveTab] = useState('');
  const [isDropdownSelected, setIsDropdownSelected] = useState(false);
  const [hasSearchResult, setHasSearchResult] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filteredByCategories, setFilteredByCategories] = useState<{ category: string; docs: vp.PortalDocument[] }[]>([]);
  const [filteredByKeyWordLists, setFilteredByKeyWordLists] = useState<{ [key: string]: vp.PortalDocument }>({});
  const [selectedDocument, setSelectedDocument] = useState<vp.PortalDocument | null>(null);
  const [activeCategoryMenu, setActiveCategoryMenu] = useState<string | null>(null);
  const isPartnerDoc = (doc: vp.PortalDocument) => {
    const portalMatch = !doc.portal || doc.portal.toLowerCase() === 'partner-dashboard';
    const accountTypeMatch =
      !doc.accountTypes?.length ||
      doc.accountTypes.some((type) => type.toLowerCase() === partnerTypeLabel.toLowerCase());
    return portalMatch && accountTypeMatch;
  };

  useEffect(() => {
    fetchPortalDocument();
  }, []);

  useEffect(() => {
    if (!documents.Documents) return;

    const portalDocs = documents.Documents.filter(isPartnerDoc);

    if (portalDocs.length >= 1) {
      if (!documents.Categories) return;

      const tabCategories = [...new Set(documents.Categories.map((doc) => doc.Category))].filter(
        (cat) => !getDisabledTab(cat, documents.Documents),
      );

      const tabs = tabCategories.map((category) => ({
        label: category,
        isDisabled: false,
        onClick: () => handleTabClick(category),
      }));

      if (tabs.length) setActiveTab(tabs[0].label);
    }
  }, [documents]);

  useEffect(() => {
    if (activeTab === '') return;

    if (!documents.Documents) return;

    const catEntries = documents.Categories || [];

    const grouped = documents.Documents
      .filter(isPartnerDoc)
      .reduce<Record<string, vp.PortalDocument[]>>((accumulator, doc) => {
        const category = doc.category?.toLowerCase();
        if (category && !accumulator[category]) accumulator[category] = [];
        if (category) accumulator[category].push(doc);
        return accumulator;
      }, {});

    const toOrder = (v: number | undefined) => (v ? v : Infinity);
    const sortedFilteredByCategories = Object.entries(grouped)
      .sort(([keyA], [keyB]) => {
        const catA = catEntries.find((c) => c.Category.toLowerCase() === keyA);
        const catB = catEntries.find((c) => c.Category.toLowerCase() === keyB);
        return toOrder(catA?.sort_order) - toOrder(catB?.sort_order);
      })
      .map(([key, docs]) => {
        const cat = catEntries.find((c) => c.Category.toLowerCase() === key);
        const sortedDocs = [...docs].sort((a, b) => toOrder(a.sort_order) - toOrder(b.sort_order));
        return { category: cat?.Category ?? key, docs: sortedDocs };
      });

    setFilteredByCategories(sortedFilteredByCategories);
  }, [activeTab, selectedDocument, document]);

  useEffect(() => {
    if (!isDropdownSelected) {
      if (!documents.Documents) return;
      const portalDocs = documents.Documents.filter(isPartnerDoc);
      if (portalDocs.length < 1) return;
      if (!activeTab) return;

      const doc = portalDocs.find((d) => d.category.toLowerCase().includes(activeTab.toLowerCase()));

      if (doc) {
        setSelectedDocument(doc);
      }
    }
  }, [activeTab, documents]);

  useEffect(() => {
    if (!selectedDocument || !selectedDocument.category) return;
    setHasSearchResult(false);
  }, [selectedDocument]);

  useEffect(() => {
    if (searchTerm === '') setHasSearchResult(false);
  }, [searchTerm]);

  const filteredDocumentsForSearch = useMemo(() => {
    if (!documents.Documents) return [];
    return documents.Documents.filter((doc) => isPartnerDoc(doc));
  }, [documents.Documents]);

  const getSelectedDocument = (docu: vp.PortalDocument) => {
    if (activeCategoryMenu !== docu.category) {
      handleMenuClick(docu.category);
    }
    setActiveTab(docu.category || '');
    setSelectedDocument(docu);
    setIsDropdownSelected(true);
    setHasSearchResult(false);
    setSearchTerm('');
  };

  const handleMenuClick = (menu: string) => {
    setActiveCategoryMenu((prev) => (prev === menu ? null : menu));
  };

  const PDFviewer = ({ docu }: { docu: vp.PortalDocument }) => (
    <>
      <h4>{docu.title}</h4>
      <div className={styles.PDFviewer}>
        <div className={styles.modalButton}>
          <Button
            id="open-pdf-modal"
            label=""
            background="transparent"
            width="18px"
            height="18px"
            size="xlg"
            icon="expand"
            color="none-light"
            iconLocation="center"
            isCircle
            onClick={() => {
              setIsModalVisible(!isModalVisible);
            }}
          />
        </div>
        <object
          data={`${docu.url}#toolbar=0&zoom=75`}
          type="application/pdf"
          style={{ width: '90%', height: '100%' }}
        />
      </div>
    </>
  );

  const MemoizedPDFviewer = useMemo(() => {
    if (selectedDocument) {
      return <PDFviewer docu={selectedDocument} />;
    }

    return null;
  }, [selectedDocument]);

  return (
    <Box flx="col">
      <ViewPDFModal
        isVisible={isModalVisible}
        pdfPath={selectedDocument?.url || ''}
        onDisagree={() => {
          setIsModalVisible(false);
        }}
      />
      <Box mb="md" jc="sb">
        <Title title="PDF Guides" />
      </Box>
      <Box flx="row" jc="sb" ai="center" mt="md" gap="md">
        <Box lineHeight="25px">
          Explore step-by-step guides designed to guide you through the VoPay Portal with ease.
        </Box>
        <PortalGuideForm
          documents={filteredDocumentsForSearch}
          filteredByKeyWordLists={filteredByKeyWordLists}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setFilteredByKeyWordLists={setFilteredByKeyWordLists}
          setHasSearchResult={setHasSearchResult}
          getSelectedDocument={getSelectedDocument}
        />
      </Box>
      {filteredByCategories.length >= 1 && (
        <div className={styles.guideContainer}>
          <div className={[styles.guideListsContainer, styles.pdfListContainer].join(' ')}>
            <div className={styles.guideScrollableList}>
              {filteredByCategories.map(({ category, docs }) => {
                const isSingleItem = docs.length === 1;
                return (
                  <div key={category} className={styles.categoryContainer}>
                    <div
                      className={`${styles.categoryTitle} ${
                        activeCategoryMenu === category || (isSingleItem && selectedDocument?.title === docs[0].title)
                          ? styles.activeCategoryMenu
                          : ''
                      }`}
                      onClick={() => {
                        if (isSingleItem) {
                          setActiveCategoryMenu(null);
                          setSelectedDocument(docs[0]);
                        } else {
                          handleMenuClick(category);
                          setSelectedDocument(docs[0]);
                        }
                      }}
                    >
                      <h4>{category}</h4>
                      {!isSingleItem &&
                        (activeCategoryMenu === category ? (
                          <Icon icon="angleTop" size="xsm" />
                        ) : (
                          <Icon icon="angleDown" size="xsm" />
                        ))}
                    </div>
                    {!isSingleItem && activeCategoryMenu === category && (
                      <div className={styles.documentsList}>
                        {docs.map((doc) => (
                          <div
                            key={doc.title}
                            className={[styles.guideList, styles[getActiveClass(doc.title)]].join(' ')}
                            onClick={() => setSelectedDocument(doc)}
                          >
                            <h5 className={styles.details}>
                              {doc.title === selectedDocument?.title ? <span></span> : null}
                              {doc.title}
                            </h5>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {hasSearchResult ? (
            <PortalGuideResult
              searchTerm={searchTerm}
              filteredByKeyWordLists={filteredByKeyWordLists}
              getSelectedDocument={getSelectedDocument}
            />
          ) : (
            <div className={styles.guide}>{selectedDocument?.title !== '' && MemoizedPDFviewer}</div>
          )}
        </div>
      )}
    </Box>
  );

  function getActiveClass(title: string) {
    if (!selectedDocument) return '';

    return title === selectedDocument.title && !hasSearchResult ? 'active' : '';
  }

  function handleTabClick(tab: string) {
    setActiveTab(tab);
    setIsDropdownSelected(false);
    setHasSearchResult(false);
    setSearchTerm('');
  }

  function getDisabledTab(label: string, docs: vp.PortalDocument[]) {
    return !Object.values(docs).some((docu) => docu.category === label);
  }
};

export default PortalGuideContainer;
